/**
 * VFXSubsystem — GPU particle effects for the player runtime.
 *
 * On level load: scans all GameObjects for `core.VFXEmitter` components,
 * resolves asset GUIDs through the manifest, fetches pre-compiled VFX
 * bundles, and creates GPU emitters via VFXEmitterManager.
 *
 * On each update: syncs emitter positions from transforms and steps the
 * particle simulation (compute shaders).
 *
 * Provides render() and getLightEmittingSystems() for GameViewRenderer
 * callbacks.
 *
 * Requires WebGPU backend with compute shader support.
 * Zero editor dependencies.
 *
 * @module @engine/player
 */

import {
  Subsystem,
  SubsystemPriority,
  type SubsystemConfig,
  type Level,
  type GameObject,
} from '@engine/core';

import {
  VFXEmitterManager,
  loadTextureFromURL,
  DEFAULT_TEXTURE_PATH,
  getVFXShader,
  registerVFXShaders,
  type CompiledDataResult,
  type CompiledVFXBundle,
  type LightEmittingSystemInfo,
} from '@engine/vfx';

import type {
  IRenderContext,
  TextureHandle,
  LightEmittingSystem,
} from '@engine/render2d';

import type { AssetSubsystem } from './AssetSubsystem';
import type { RenderSubsystem } from './RenderSubsystem';

// ============================================================================
// TYPES
// ============================================================================

interface EmitterMapping {
  emitterId: string;
  vfxGuid: string;
}

// ============================================================================
// OPTIONS
// ============================================================================

export interface VFXSubsystemOptions {
  /** Enable debug logging (default: false) */
  debug?: boolean;
}

// ============================================================================
// SUBSYSTEM
// ============================================================================

export class VFXSubsystem extends Subsystem {
  readonly config: SubsystemConfig = {
    name: 'vfx',
    // After scripts (200) and redprint (201), before TransformSync (210)
    priority: SubsystemPriority.Runtime + 5,
    optional: true,
    dependencies: ['render', 'assets'],
  };

  private readonly options: VFXSubsystemOptions;

  /** Core emitter manager (handles all GPU work) */
  private manager: VFXEmitterManager | null = null;
  private renderContext: IRenderContext | null = null;
  private defaultTexture: TextureHandle | null = null;

  /** GUID → emitter mapping */
  private guidToEmitter: Map<string, EmitterMapping> = new Map();

  /** Current level reference */
  private level: Level | null = null;

  /** SceneEvent unsubscribe functions */
  private _eventUnsubs: (() => void)[] = [];

  /** Debug flag */
  private debug: boolean;

  constructor(options: VFXSubsystemOptions = {}) {
    super();
    this.options = options;
    this.debug = options.debug ?? false;
  }

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  async onInitialize(): Promise<void> {
    const renderSub = this.getSubsystem<RenderSubsystem>('render');
    const ctx = renderSub?.getContext();
    if (!ctx) {
      console.warn('[VFXSubsystem] No render context available, VFX disabled.');
      return;
    }

    this.renderContext = ctx;

    if (!ctx.supportsCompute) {
      console.warn('[VFXSubsystem] Compute shaders not supported, VFX disabled.');
      return;
    }

    // Pre-load default VFX texture (128×128 smooth circle)
    // Uses embeddedAssets for automatic dev/prod path resolution
    try {
      this.defaultTexture = await loadTextureFromURL(ctx, DEFAULT_TEXTURE_PATH, {
        label: 'vfx-default-texture',
      });
    } catch (e) {
      console.warn('[VFXSubsystem] Failed to load default texture.', (e as Error).message, (e as Error).stack);
    }

    console.log('[VFXSubsystem] Initialized (compute available)');
  }

  onUpdate(dt: number): void {
    if (!this.manager || !this.level) return;

    // Sync emitter positions from transforms
    for (const [goId, mapping] of this.guidToEmitter) {
      const go = this.level.findByGuid(goId);
      if (!go) continue;

      const worldPos = go.transform.position;
      this.manager.setPosition(mapping.emitterId, worldPos.x, worldPos.y);

      const scale = go.transform.lossyScale;
      this.manager.setScale(mapping.emitterId, scale.x, scale.y);
    }

    // Step particle simulation
    this.manager.update(dt);

    // Flush compute to ensure particle buffers are committed before light
    // injection reads them (otherwise VFX lights lag one frame).
    if (this.renderContext?.flushCompute) {
      this.renderContext.flushCompute();
    }
  }

  onShutdown(): void {
    this.destroyManager();
    if (this.defaultTexture && this.renderContext) {
      this.renderContext.destroyTexture(this.defaultTexture);
      this.defaultTexture = null;
    }
    this.renderContext = null;
  }

  // ==========================================================================
  // LEVEL MANAGEMENT
  // ==========================================================================

  /**
   * Called by LevelSubsystem when a new level is loaded.
   * Scans for VFXEmitter components and creates GPU emitters.
   */
  async setLevel(level: Level | null): Promise<void> {
    // Tear down previous level's emitters
    this.destroyManager();
    this.level = level;

    if (!level || !this.renderContext || !this.renderContext.supportsCompute) return;

    const assets = this.getSubsystem<AssetSubsystem>('assets');
    const manifest = assets?.getManifest();
    if (!manifest) return;

    // Create new manager
    this.manager = new VFXEmitterManager(this.renderContext, {
      // Graph source loader — unused in player (everything is pre-compiled)
      graphLoader: async () => null,
      compiledDataLoader: (path: string) => this.loadCompiledBundle(path),
      textureResolver: (guid: string) => this.resolveTexture(guid),
      debug: this.debug,
    });

    if (this.defaultTexture) {
      this.manager.setDefaultTexture(this.defaultTexture);
    }

    // Scan all GameObjects for core.VFXEmitter components
    const allObjects = level.getAllObjects();
    const emitterPromises: Promise<void>[] = [];

    for (const go of allObjects) {
      const vfxData = go.getComponentData('core.VFXEmitter');
      if (!vfxData) continue;

      const vfxGuid = vfxData.vfxAsset as string | undefined;
      if (!vfxGuid) {
        if (this.debug) console.warn(`[VFX] ${go.name} has no VFX asset, skipping.`);
        continue;
      }

      // Resolve GUID to exported path via manifest
      const vfxPath = manifest.assets.vfx?.[vfxGuid];
      if (!vfxPath) {
        console.warn(`[VFX] Asset ${vfxGuid} not in manifest, skipping.`);
        continue;
      }

      const worldPos = go.transform.position;
      const variableOverrides = (vfxData.variableOverrides as Record<string, unknown>) ?? {};

      const promise = this.manager.createEmitter(vfxGuid, {
        x: worldPos.x,
        y: worldPos.y,
        playOnStart: (vfxData.playOnAwake as boolean) ?? true,
        loop: (vfxData.loop as boolean) ?? true,
        playbackSpeed: (vfxData.playbackSpeed as number) ?? 1,
      }).then(emitterId => {
        this.guidToEmitter.set(go.guid, { emitterId, vfxGuid });

        // Apply variable overrides
        this.applyVariableOverrides(emitterId, vfxGuid, variableOverrides);
      }).catch(err => {
        console.error(`[VFX] Failed to create emitter for ${vfxGuid}:`, err);
      });

      emitterPromises.push(promise);
    }

    if (emitterPromises.length > 0) {
      await Promise.all(emitterPromises);
      console.log(`[VFXSubsystem] Created ${emitterPromises.length} emitter(s)`);
    }

    // Subscribe to runtime scene changes
    this._eventUnsubs.push(
      level.events.onObjectAdded(({ gameObject }) => {
        this._createEmitterForObject(gameObject);
      }, ['core.VFXEmitter']),
      level.events.onObjectRemoved(({ gameObject }) => {
        this._removeEmitterForObject(gameObject.guid);
      }),
    );
  }

  // ==========================================================================
  // RENDER API (called by RenderSubsystem callbacks)
  // ==========================================================================

  /**
   * Render all active VFX emitters.
   * Bound to GameViewRenderer.setVFXRenderCallback.
   */
  render(viewProjection: Float32Array, ctx?: IRenderContext, isHdrTarget = false): void {
    if (!this.manager) return;
    this.manager.render(viewProjection, ctx, isHdrTarget);
  }

  /**
   * Get light-emitting particle systems for VFXLightInjectPass.
   * Bound to GameViewRenderer.setVFXLightInjectCallback.
   */
  getLightEmittingSystems(): LightEmittingSystem[] {
    if (!this.manager) return [];
    return this.manager.getLightEmittingSystems() as LightEmittingSystem[];
  }

  // ==========================================================================
  // LOADERS
  // ==========================================================================

  /**
   * Load a pre-compiled VFX bundle from the exported assets.
   * The `path` here is the VFX GUID (used as the key in createEmitter).
   */
  private async loadCompiledBundle(path: string): Promise<CompiledDataResult | null> {
    const assets = this.getSubsystem<AssetSubsystem>('assets');
    const manifest = assets?.getManifest();
    if (!manifest || !assets) return null;

    // path is the GUID — resolve to exported path
    const relativePath = manifest.assets.vfx?.[path];
    if (!relativePath) return null;

    try {
      const url = assets.resolveUrl(relativePath);
      const resp = await fetch(url);
      if (!resp.ok) return null;

      const bundle = (await resp.json()) as CompiledVFXBundle;
      if (!bundle.systems || bundle.systems.length === 0) return null;

      // Populate the VFX shader cache from the build snapshot, then resolve stage shaders by id.
      await this.ensureVFXShaders();

      const result: CompiledDataResult = {
        systems: bundle.systems.map(sys => ({
          id: sys.id,
          name: sys.name,
          initShader: getVFXShader(sys.initShaderId),
          updateShader: getVFXShader(sys.updateShaderId),
          vertexShader: getVFXShader(sys.vertexShaderId),
          fragmentShader: getVFXShader(sys.fragmentShaderId),
          maxParticles: sys.maxParticles,
          spawnType: sys.spawn?.mode,
          spawnRate: sys.spawn?.rate,
          burst: sys.spawn?.burst,
          usesTexture: sys.usesTexture,
          textureSlot: sys.textureSlot,
          emitsLight: sys.emitsLight,
          lightRenderQuad: sys.lightRenderQuad,
          lightConfig: sys.lightConfig,
          isLit: sys.isLit,
        })),
        particleStruct: bundle.particleStruct,
      };

      return result;
    } catch (err) {
      console.error(`[VFX] Failed to load compiled bundle for ${path}:`, err);
      return null;
    }
  }

  private vfxShadersLoading: Promise<void> | null = null;

  /**
   * Populate the VFX shader cache from the build-emitted `vfx-shaders.json` (once). MEMOIZED as a
   * shared Promise: `setLevel` creates all emitters concurrently, so multiple callers race here —
   * the old `loaded=true`-before-await let a later caller reach `getVFXShader` with an empty cache.
   * Loads host-aware via `assets.loadJson` (the native player has NO `fetch`; a raw fetch silently
   * failed there, leaving the cache empty → `getVFXShader` threw `shader not registered`).
   */
  private ensureVFXShaders(): Promise<void> {
    if (this.vfxShadersLoading) return this.vfxShadersLoading;
    this.vfxShadersLoading = (async () => {
      const assets = this.getSubsystem<AssetSubsystem>('assets');
      const rel = assets?.getManifest()?.vfxShaders;
      if (!rel || !assets) return;
      try {
        registerVFXShaders(await assets.loadJson<Record<string, string>>('__vfx_shaders__', rel));
      } catch (err) {
        console.error('[VFX] Failed to load vfx-shaders.json:', err);
      }
    })();
    return this.vfxShadersLoading;
  }

  /**
   * Resolve a texture asset GUID to a GPU texture handle.
   * Uses the same manifest-based resolution as RenderSubsystem.
   */
  private async resolveTexture(guid: string): Promise<TextureHandle | null> {
    const renderSub = this.getSubsystem<RenderSubsystem>('render');
    if (!renderSub) return null;

    // The render subsystem's GameViewRenderer has internal texture loading
    // Use the same approach: resolve GUID → path via manifest → load
    const assets = this.getSubsystem<AssetSubsystem>('assets');
    const manifest = assets?.getManifest();
    if (!manifest || !assets) return null;

    const texPath = manifest.assets.textures?.[guid];
    if (!texPath || !this.renderContext) return null;

    try {
      const url = assets.resolveUrl(texPath);
      const { loadTextureFromURL } = await import('@engine/vfx');
      return await loadTextureFromURL(this.renderContext, url, { label: `vfx-tex-${guid}` });
    } catch {
      return null;
    }
  }

  // ==========================================================================
  // VARIABLE OVERRIDES
  // ==========================================================================

  /**
   * Apply variable overrides from VFXEmitter component to an emitter.
   */
  private applyVariableOverrides(
    emitterId: string,
    vfxGuid: string,
    overrides: Record<string, unknown>,
  ): void {
    if (!this.manager || Object.keys(overrides).length === 0) return;

    const variables = this.manager.getGraphVariables(vfxGuid);
    if (!variables) return;

    // Build index-by-type map
    const typeCounters: Record<string, number> = {};
    const varMap = new Map<string, { type: string; index: number }>();

    for (const v of variables) {
      const typeKey = v.type === 'int' ? 'float' : v.type;
      const index = typeCounters[typeKey] ?? 0;
      typeCounters[typeKey] = index + 1;
      varMap.set(v.id, { type: v.type, index });
    }

    for (const [varId, value] of Object.entries(overrides)) {
      const varDef = varMap.get(varId);
      if (!varDef || value === undefined || value === null) continue;

      switch (varDef.type) {
        case 'float':
        case 'int':
          this.manager.setFloat(emitterId, varDef.index, value as number);
          break;
        case 'vec2': {
          const v2 = value as { x?: number; y?: number };
          this.manager.setVec2(emitterId, varDef.index, v2.x ?? 0, v2.y ?? 0);
          break;
        }
        case 'vec3': {
          const v3 = value as { x?: number; y?: number; z?: number };
          this.manager.setVec3(emitterId, varDef.index, v3.x ?? 0, v3.y ?? 0, v3.z ?? 0);
          break;
        }
        case 'color': {
          const c = value as { r?: number; g?: number; b?: number; a?: number };
          this.manager.setColor(emitterId, varDef.index, c.r ?? 1, c.g ?? 1, c.b ?? 1, c.a ?? 1);
          break;
        }
        case 'texture2d': {
          const textureGuid = value as string;
          if (textureGuid) {
            this.resolveTexture(textureGuid).then(handle => {
              if (handle && this.manager) {
                this.manager.setTexture(emitterId, varDef.index, handle);
              }
            }).catch(() => {});
          }
          break;
        }
        default:
          break;
      }
    }
  }

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  private destroyManager(): void {
    for (const unsub of this._eventUnsubs) unsub();
    this._eventUnsubs = [];
    if (this.manager) {
      this.manager.destroy();
      this.manager = null;
    }
    this.guidToEmitter.clear();
  }

  /** Create a VFX emitter for a runtime-spawned object */
  private _createEmitterForObject(go: GameObject): void {
    if (!this.manager) return;
    const vfxData = go.getComponentData('core.VFXEmitter');
    if (!vfxData) return;

    const vfxGuid = vfxData.vfxAsset as string | undefined;
    if (!vfxGuid) return;

    const assets = this.getSubsystem<AssetSubsystem>('assets');
    const manifest = assets?.getManifest();
    const vfxPath = manifest?.assets.vfx?.[vfxGuid];
    if (!vfxPath) return;

    const worldPos = go.transform.position;
    const variableOverrides = (vfxData.variableOverrides as Record<string, unknown>) ?? {};

    this.manager.createEmitter(vfxGuid, {
      x: worldPos.x,
      y: worldPos.y,
      playOnStart: (vfxData.playOnAwake as boolean) ?? true,
      loop: (vfxData.loop as boolean) ?? true,
      playbackSpeed: (vfxData.playbackSpeed as number) ?? 1,
    }).then(emitterId => {
      this.guidToEmitter.set(go.guid, { emitterId, vfxGuid });
      this.applyVariableOverrides(emitterId, vfxGuid, variableOverrides);
    }).catch(err => {
      console.error(`[VFX] Failed to create runtime emitter for ${vfxGuid}:`, err);
    });
  }

  /** Remove VFX emitter for a removed object */
  private _removeEmitterForObject(guid: string): void {
    const mapping = this.guidToEmitter.get(guid);
    if (mapping && this.manager) {
      this.manager.destroyEmitter(mapping.emitterId);
      this.guidToEmitter.delete(guid);
    }
  }

  // ── GUID-addressed control surface (GuidVFXApi) — backs engine.vfx for scripts ──
  play(guid: string): void {
    const m = this.guidToEmitter.get(guid);
    if (m && this.manager) this.manager.play(m.emitterId);
  }
  stop(guid: string): void {
    const m = this.guidToEmitter.get(guid);
    if (m && this.manager) this.manager.stop(m.emitterId);
  }
  spawn(guid: string, count: number): void {
    const m = this.guidToEmitter.get(guid);
    if (m && this.manager) this.manager.spawn(m.emitterId, count);
  }
  setLoop(guid: string, loop: boolean): void {
    const m = this.guidToEmitter.get(guid);
    if (m && this.manager) this.manager.setLoop(m.emitterId, loop);
  }
  isComplete(guid: string): boolean {
    const m = this.guidToEmitter.get(guid);
    return !!(m && this.manager) && this.manager.isEmitterComplete(m.emitterId);
  }
  setVariable(guid: string, name: string, value: unknown): boolean {
    const m = this.guidToEmitter.get(guid);
    return !!(m && this.manager) && this.manager.setVariable(m.emitterId, name, value);
  }
}
